import { getThermostatMode } from '../../lib/capabilities/thermostat';
import { round } from '../../lib/math';
import { Glyph } from '../common/Glyph';

import { useCapabilityValue } from '../../store/devices/useCapabilityValue';

import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';
import styles from './IndicatorThermostat.module.css';

const defaultThermostatCapabilityIds = {
  measureTemperature: 'measure_temperature',
  targetTemperature: 'target_temperature',
  thermostatMode: 'thermostat_mode',
};

const thermostatStatusGlyphs = {
  cool: {
    backgroundColor: theme.color.blue,
    source: glyph.core.arrowDownBold,
  },
  heat: {
    backgroundColor: theme.color.orange,
    source: glyph.core.arrowUpBold,
  },
};

export function IndicatorThermostat(props) {
  const thermostatCapabilityIds = getThermostatCapabilityIds(props.capabilityId);
  const [measureTemperatureValue, , measureTemperatureCapabilityObj] = useCapabilityValue({
    deviceId: props.device.id,
    capabilityId: thermostatCapabilityIds.measureTemperature,
  });
  const [targetTemperatureValue, , targetTemperatureCapabilityObj] = useCapabilityValue({
    deviceId: props.device.id,
    capabilityId: thermostatCapabilityIds.targetTemperature,
  });
  const [thermostatModeValue] = useCapabilityValue({
    deviceId: props.device.id,
    capabilityId: thermostatCapabilityIds.thermostatMode,
  });

  const mode = getIndicatorThermostatMode({
    mode: thermostatModeValue,
    measure: measureTemperatureValue,
    target: targetTemperatureValue,
  });

  const targetTemperatureLabel =
    targetTemperatureCapabilityObj != null ? formatTemperatureLabel(targetTemperatureValue) : null;
  const measureTemperatureLabel =
    measureTemperatureCapabilityObj != null
      ? formatTemperatureLabel(measureTemperatureValue)
      : null;
  const statusGlyph = getThermostatStatusGlyph(mode);
  const activeColor = getThermostatPrimaryTextColor({ mode });
  const idleTemperatureLabel = getIdleTemperatureLabel({
    targetTemperatureLabel,
    measureTemperatureLabel,
  });
  const shouldShowSingleValue = mode === 'idle';
  const shouldShowSplitValues = mode !== 'idle';

  if (targetTemperatureLabel == null && measureTemperatureLabel == null) {
    return null;
  }

  return (
    <div
      className={styles.root}
      style={{
        '--prop-primary-text-color': activeColor,
        '--prop-secondary-text-color': theme.color.mono_400,
        '--prop-status-icon-background-color':
          statusGlyph != null ? statusGlyph.backgroundColor : 'transparent',
      }}
    >
      {statusGlyph != null ? (
        <span aria-hidden={true} className={styles.statusIcon}>
          <Glyph
            className={styles.statusGlyph}
            source={statusGlyph.source}
            color={theme.color.mono_000}
            size="10px"
          />
        </span>
      ) : null}

      {shouldShowSingleValue === true && idleTemperatureLabel != null ? (
        <div className={styles.singleValue}>{idleTemperatureLabel}</div>
      ) : null}

      {shouldShowSplitValues === true && targetTemperatureLabel != null ? (
        <div className={styles.primaryValue}>{targetTemperatureLabel}</div>
      ) : null}

      {shouldShowSplitValues === true &&
      targetTemperatureLabel != null &&
      measureTemperatureLabel != null ? (
        <div className={styles.separator}>•</div>
      ) : null}

      {shouldShowSplitValues === true && measureTemperatureLabel != null ? (
        <div className={styles.secondaryValue}>{measureTemperatureLabel}</div>
      ) : null}
    </div>
  );
}

function getThermostatCapabilityIds(capabilityId) {
  if (capabilityId == null) {
    return defaultThermostatCapabilityIds;
  }

  const [, subcapabilityId] = capabilityId.split('.');

  if (subcapabilityId == null) {
    return defaultThermostatCapabilityIds;
  }

  return {
    measureTemperature: `measure_temperature.${subcapabilityId}`,
    targetTemperature: `target_temperature.${subcapabilityId}`,
    thermostatMode: `thermostat_mode.${subcapabilityId}`,
  };
}

function getIndicatorThermostatMode({ mode, measure, target }) {
  const activeMode = getThermostatMode({ mode, measure, target });

  if (activeMode !== 'off') {
    return activeMode;
  }

  if (measure == null || target == null) {
    return 'off';
  }

  if (mode === 'off') {
    return 'off';
  }

  if (mode == null || mode === 'auto' || mode === 'cool' || mode === 'heat') {
    return 'idle';
  }

  return 'off';
}

function getThermostatStatusGlyph(mode) {
  if (mode == null) {
    return null;
  }

  if (mode in thermostatStatusGlyphs) {
    return thermostatStatusGlyphs[mode];
  }

  return null;
}

function getThermostatPrimaryTextColor({ mode }) {
  if (mode === 'off') {
    return theme.color.mono_400;
  }

  if (mode === 'cool') {
    return theme.color.blue;
  }

  if (mode === 'heat') {
    return theme.color.orange;
  }

  return theme.color.mono_400;
}

function getIdleTemperatureLabel({ targetTemperatureLabel, measureTemperatureLabel }) {
  if (measureTemperatureLabel != null) {
    return measureTemperatureLabel;
  }

  return targetTemperatureLabel;
}

function formatTemperatureLabel(value) {
  if (typeof value !== 'number') {
    return null;
  }

  return `${round(value, 1)}°`;
}
